<template>
  <div>
    <EdroneCheckbox
      :checked="hasRegistrationCode"
      :label="translation.registrationCode?.checkbox || 'I have a code'"
      class="tw:mt-2"
      unique-id="RegisterForm_hasRegistrationCode"
      @change="hasRegistrationCode = $event"
    />

    <div v-if="hasRegistrationCode" class="tw:mt-2">
      <EdroneInput
        :label="translation.registrationCode?.label || 'Registration code'"
        :placeholder="translation.registrationCode?.placeholder || 'Enter your code'"
        :value="registrationCode"
        name="registrationCode"
        @input="handleRegistrationCodeInput"
      />
      <p v-if="registrationCodeStatus === 'validating'" class="tw:caption tw:!text-default-700 tw:mt-1">
        {{ translation.registrationCode?.validating || "Validating..." }}
      </p>
      <p v-else-if="registrationCodeStatus === 'valid'" class="tw:caption tw:!text-green-600 tw:mt-1">
        {{ translation.registrationCode?.valid || "Code approved" }}
      </p>
      <p v-else-if="registrationCodeStatus === 'invalid'" class="tw:caption tw:!text-red-500 tw:mt-1">
        {{ translation.registrationCode?.invalid || "Code not valid" }}
      </p>
      <p v-else-if="registrationCodeStatus === 'expired'" class="tw:caption tw:!text-red-500 tw:mt-1">
        {{ translation.registrationCode?.expired || "Code has expired" }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { watch, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useRegisterStore, type RegistrationCodeStatus } from "vue-app-new/modules/Register/store/Register.store";
import { validateRegistrationCode as validateRegistrationCodeApi } from "vue-app-new/api/Register.service";
import EdroneCheckbox from "vue-app/edroneComponents/EdroneCheckbox.vue";
import EdroneInput from "vue-app/edroneComponents/EdroneInput.vue";

const props = defineProps<{
  initialCode?: string;
}>();

const translation = window._trans._register.form;

const store = useRegisterStore();
const { hasRegistrationCode, registrationCode, registrationCodeStatus } = storeToRefs(store);

let registrationCodeValidationTimeout: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  if (props.initialCode) {
    store.hasRegistrationCode = true;
    store.registrationCode = props.initialCode;
    validateRegistrationCode(props.initialCode);
  }
});

const validateRegistrationCode = async (code: string) => {
  if (!code.trim()) {
    store.registrationCodeStatus = "idle";
    return;
  }

  store.registrationCodeStatus = "validating";

  try {
    const data = await validateRegistrationCodeApi(code);

    const statusMap: Record<string, RegistrationCodeStatus> = {
      VALID: "valid",
      NOT_FOUND: "invalid",
      EXPIRED: "expired",
    };

    store.registrationCodeStatus = statusMap[data.status] || "invalid";
  } catch {
    store.registrationCodeStatus = "invalid";
  }
};

const handleRegistrationCodeInput = (value: string | string[]) => {
  const stringValue = Array.isArray(value) ? value[0] || "" : value;
  store.registrationCode = stringValue;

  if (registrationCodeValidationTimeout) {
    clearTimeout(registrationCodeValidationTimeout);
  }

  if (!stringValue.trim()) {
    store.registrationCodeStatus = "idle";
    return;
  }

  store.registrationCodeStatus = "validating";

  registrationCodeValidationTimeout = setTimeout(() => {
    validateRegistrationCode(stringValue);
  }, 500);
};

watch(hasRegistrationCode, (newValue) => {
  if (!newValue) {
    store.registrationCode = "";
    store.registrationCodeStatus = "idle";
  }
});
</script>

